import React, { useState } from 'react';
import { Button, Card, Space, Tabs, Typography } from 'antd';
import { BulbOutlined, PlusOutlined } from '@ant-design/icons';
import type { GoogleUser } from '../App';
import FeatureRequestBoard from './FeatureRequestBoard';
import FeatureRequestDetails from './FeatureRequestDetails';
import FeatureRequestForm from './FeatureRequestForm';
import MyFeatureRequests from './MyFeatureRequests';
import FeatureRoadmap from './FeatureRoadmap';
import AdminFeatureGovernance from './AdminFeatureGovernance';

const { Title, Text } = Typography;

type Tab = 'board' | 'roadmap' | 'my' | 'following' | 'submit' | 'details' | 'governance';

interface Props {
  user: GoogleUser | null;
  isAdmin?: boolean;
}

const NAV_TABS = (isAdmin: boolean) => [
  { key: 'board', tab: 'Community Board' },
  { key: 'roadmap', tab: 'Roadmap' },
  { key: 'my', tab: 'My Requests' },
  { key: 'following', tab: 'Following / Voted' },
  ...(isAdmin ? [{ key: 'governance', tab: 'Feature Governance' }] : []),
];

const FeatureRequestsHub: React.FC<Props> = ({ user, isAdmin = false }) => {
  const [tab, setTab] = useState<Tab>('board');
  const [selectedId, setSelectedId] = useState('');
  const adminEmail = user?.email || '';

  const openDetails = (id: string) => {
    if (id === 'new') {
      setTab('submit');
      return;
    }
    setSelectedId(id);
    setTab('details');
  };

  if (tab === 'submit') {
    return (
      <div className="praxys-feature-page kb-page">
        <FeatureRequestForm onCreated={() => setTab('my')} onCancel={() => setTab('board')} />
      </div>
    );
  }

  if (tab === 'details') {
    return (
      <div className="praxys-feature-page kb-page">
        <FeatureRequestDetails
          featureRequestId={selectedId}
          isAdmin={isAdmin}
          adminEmail={adminEmail}
          onBack={() => setTab('board')}
        />
      </div>
    );
  }

  return (
    <div className="praxys-feature-page kb-page">
      <div className="praxys-feature-header kb-header">
        <div className="kb-hero">
          <BulbOutlined className="kb-hero-icon praxys-feature-hero-icon" />
          <div>
            <Title level={3} style={{ margin: 0, color: '#e6edf3' }}>Feature Requests</Title>
            <Text className="kb-subtitle">
              Suggest, vote, and track community-driven platform improvements.
            </Text>
          </div>
        </div>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openDetails('new')}>
            Create Feature Request
          </Button>
        </Space>
      </div>

      <Card className="kb-card praxys-feature-tabs-card" bodyStyle={{ padding: '0 14px' }}>
        <Tabs
          className="kb-tabs"
          activeKey={tab}
          onChange={key => setTab(key as Tab)}
          style={{ marginBottom: 4 }}
        >
          {NAV_TABS(isAdmin).map(t => (
            <Tabs.TabPane key={t.key} tab={t.tab} />
          ))}
        </Tabs>
      </Card>

      <div className="kb-workspace praxys-feature-workspace">
        {tab === 'board' && <FeatureRequestBoard onOpenDetails={openDetails} />}
        {tab === 'roadmap' && <FeatureRoadmap isAdmin={isAdmin} adminEmail={adminEmail} onOpenDetails={openDetails} />}
        {tab === 'my' && <MyFeatureRequests onOpenDetails={openDetails} />}
        {tab === 'following' && <FeatureRequestBoard onOpenDetails={openDetails} defaultFollowing />}
        {tab === 'governance' && isAdmin && (
          <AdminFeatureGovernance adminEmail={adminEmail} onOpenDetails={openDetails} />
        )}
      </div>
    </div>
  );
};

export default FeatureRequestsHub;
